import { GetServerSideProps, NextPage } from 'next';
import React from 'react';

import { UserRole } from '~/api/account/accountApiTypes';
import { pagesConfigStore } from '~/atomic/atom/links/pagesConfigStore';
import { LayoutType } from '~/atomic/layout/DefaultLayout';
import { PageTemplate } from '~/atomic/organism/PageTemplate/PageTemplate';
import { getAuthorByName } from '~/atomic/page/author/author.data';
import { authorSelector } from '~/atomic/page/author/author.selector';
import { AuthorBooks } from '~/atomic/page/author/books/AuthorBooks';
import { SCAuthorPage } from '~/atomic/page/author/styled';
import { checkAuthToken } from '~/feature/authorization/checkAuthToken';
import { getAuthTokenFromServer } from '~/feature/authorization/getAuthToken';
import { getCyclesByAuthorUsername } from '~/feature/cycle/cycleSlice';
import { AuthorTemplate } from '~/feature/template/AuthorTemplate';
import { AddAuthorMetrika } from '~/feature/yandex/addAuthorMetrika';
import {
  createStore,
  RootState, useAppSelector,
} from '~/store';

const AuthorBooksPage:NextPage & LayoutType = () => {
  const { currentAuthor } = useAppSelector(authorSelector);

  const isEmpty = currentAuthor?.counters?.books === 0;

  const description = `Все произведения автора ${currentAuthor?.name} на сайте Букривер`
    + ' ⭐ Серии и циклы книг полностью ⭐ Читайте онлайн, '
    + ' добавляйте в библиотеку и скачивайте книги на нашем сайте!';

  const title = `${currentAuthor?.name}: все книги читать онлайн`;

  return (
    <PageTemplate
      isPublic={pagesConfigStore.author.isPublic}
      seo={{
        title,
        description,
        canonical: pagesConfigStore.author.as(currentAuthor.username),
        image: currentAuthor?.photoUrl,
        customSeoTags: (
          <>
            {isEmpty && (
              <meta name="robots" content="noindex, nofollow" />
            )}
          </>
        ),
      }}
      content={(
        <>
          <SCAuthorPage>
            <AuthorBooks />
          </SCAuthorPage>
          <AddAuthorMetrika metrikaId={currentAuthor?.yandexMetrika} />
        </>
      )}
    />
  );
};

AuthorBooksPage.getLayout = (page) => (
  <AuthorTemplate>{ page }</AuthorTemplate>
);

export const getServerSideProps: GetServerSideProps<
{ state: RootState; },
{ authorUsername: string; }
> = async (context) => {
  const { dispatch, store } = await createStore();
  await checkAuthToken(context, dispatch);
  const token = getAuthTokenFromServer(context);

  const authorUsername = context.params?.authorUsername ?? '';

  try {
    await Promise.all([
      dispatch(getAuthorByName({ username: authorUsername, token })),
      dispatch(getCyclesByAuthorUsername({ authorUsername, token })),
    ]);

    let state = store.getState();

    const { author: { currentAuthor } } = state;

    if (!currentAuthor?.id) {
      return { notFound: true };
    }

    if (currentAuthor.role !== UserRole.author) {
      return {
        redirect: {
          destination: pagesConfigStore.user.as(currentAuthor.username),
          permanent: false,
        },
      };
    }

    state = store.getState();

    return { props: { state } };
  } catch (error) {
    console.log('error on page', { error });
    const state = store.getState();
    return { props: { state } };
  }
};

export default AuthorBooksPage;
